import { Box, Button, Flex, Group, Text } from '@mantine/core';
import { IconFileText } from '@tabler/icons-react';
import { Link } from 'react-router';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { SuitabilityReportDocumentView, SuitabilityReportProposedAction } from '../chat/types';
import { ProposalDecidedLine } from './ProposalDecidedLine';

type Props = {
  proposal: SuitabilityReportProposedAction;
  onDecide: (approve: boolean) => void;
  // Reopens the generation flow pre-filled, for an adviser who closed it without generating.
  onReview?: () => void;
  disabled?: boolean;
};

const ROLE_HEADINGS = { ADVICE: 'Advice documents', FEE: 'Fee documents' } as const;

function reportsTabHref(proposal: SuitabilityReportProposedAction): string | null {
  if (proposal.familyId) return `/family/${proposal.familyId}/documents/suitability-reports`;
  if (proposal.clientId) return `/client/${proposal.clientId}/document/suitability-reports`;
  return null;
}

function DocumentGroup({
  role,
  documents,
  pending,
}: {
  role: SuitabilityReportDocumentView['role'];
  documents: SuitabilityReportDocumentView[];
  pending: boolean;
}) {
  if (documents.length === 0) return null;
  return (
    <Flex direction="column" gap="xxs">
      <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.7">
        {ROLE_HEADINGS[role]}
      </Text>
      {documents.map((document, index) => (
        <Box key={`${document.name}-${index}`}>
          <Text size="textSm" c="gray.9">
            {document.name}
          </Text>
          {document.generated && pending && (
            <Text size="textXs" c="gray.7">
              Will be saved to documents
            </Text>
          )}
        </Box>
      ))}
    </Flex>
  );
}

export function SuitabilityReportProposalWidget({ proposal, onDecide, onReview, disabled }: Props) {
  const isPending = proposal.status === 'PENDING' || !proposal.status;
  const href = reportsTabHref(proposal);

  return (
    <Box bg="orange.1" p="md" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
      <Flex direction="column" gap="sm">
        <Group gap="xs" align="center" wrap="nowrap">
          <IconFileText size={16} stroke={1.5} color="var(--mantine-color-orange-7)" aria-hidden />
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
            Proposed suitability report
          </Text>
        </Group>
        <Box>
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
            {proposal.templateName}
          </Text>
          <Text size="textXs" c="gray.7">
            {proposal.reportFormat === 'LETTER' ? 'Letter' : 'Report'}
          </Text>
        </Box>
        <DocumentGroup
          role="ADVICE"
          documents={proposal.documents.filter(document => document.role === 'ADVICE')}
          pending={isPending}
        />
        <DocumentGroup
          role="FEE"
          documents={proposal.documents.filter(document => document.role === 'FEE')}
          pending={isPending}
        />
        {isPending ? (
          <Group gap="sm">
            <Button
              variant="filled"
              color="orange"
              size="sm"
              onClick={() => onDecide(true)}
              disabled={disabled}
            >
              Review report
            </Button>
            <Button variant="default" size="sm" onClick={() => onDecide(false)} disabled={disabled}>
              Dismiss
            </Button>
          </Group>
        ) : (
          <Flex direction="column" gap="xs">
            {proposal.status === 'APPROVED' ? (
              <Group gap="sm" align="center">
                <Text size="textSm" fw={FONT_WEIGHTS.medium} c="success.8">
                  Opened for review
                </Text>
                {onReview && (
                  <Button variant="default" size="xs" onClick={onReview} disabled={disabled}>
                    Review report
                  </Button>
                )}
              </Group>
            ) : (
              <ProposalDecidedLine
                status={proposal.status}
                failureReason={proposal.failureReason}
                deniedLabel="Dismissed"
              />
            )}
            {proposal.status === 'APPROVED' && href && (
              <Text size="textSm" component={Link} to={href} c="orange.8">
                View suitability reports
              </Text>
            )}
          </Flex>
        )}
      </Flex>
    </Box>
  );
}
